import * as moment from 'moment';
import _ from 'lodash';
import { Component, DestroyRef, EventEmitter, forwardRef, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbDatepickerModule } from 'mdb-angular-ui-kit/datepicker';

let id = 1;

@Component({
  standalone: true,
  imports:[CommonModule,FormsModule,ReactiveFormsModule,MdbFormsModule,MdbDatepickerModule],
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatepickerComponent),
    multi: true,
  }],
})
export class DatepickerComponent implements OnInit, OnChanges, ControlValueAccessor {
  destroyRef = inject(DestroyRef)
  @Input() label: string = 'Datepicker';
  @Input() required: boolean = false;
  @Input() submitted: boolean = false;

  @Input() myDatePickerOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  };
  @Input() selectedDate = null;

  @Output() onClear: EventEmitter<any> = new EventEmitter();

  public _id = id++;
  get id() {
    return `datepicker_${this._id}`;
  }

  public control: UntypedFormControl = new UntypedFormControl(null);

  public minDate: Date = null;
  public maxDate: Date = null;
  public startDate: Date = null;

  constructor() {
    const date = moment();
    this.selectedDate = {
      date: {
        year: date.year(),
        month: date.month() + 1,
        day: date.date()
      }
    }
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(
      filter(value => value instanceof Date),
      map(value => moment(value).format("MM/DD/YYYY")),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.onChangeCallback(value);
    });
    this.control.valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.onChangeCallback(value);
      this.onClear.next(true);
    });

    if(this.required) {
      this.control.setValidators([Validators.required]);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const submitted = _.get(changes, 'submitted.currentValue', this.submitted);
    if(submitted) {
      this.control.markAsTouched();
      this.onTouchedCallback()
    }

    const myDatePickerOptions = _.get(changes, 'myDatePickerOptions.currentValue', this.myDatePickerOptions);

    if(myDatePickerOptions.disableUntil) {
      const minYear = _.get(myDatePickerOptions, 'minYear', null);
      let disableUntil = _.isNull(minYear) ? null: { year: minYear, month: 1, day: 1 };
      disableUntil = _.get(myDatePickerOptions, 'disableUntil', disableUntil);
      this.minDate = _.isNull(disableUntil) ? null: new Date(disableUntil.year, disableUntil.month - 1, disableUntil.day);
    }
    if(myDatePickerOptions.closeDate) {
      const currentDate = this.selectedDate.date
      this.minDate = _.isNull(currentDate) ? null: new Date(currentDate.year, currentDate.month - 1, currentDate.day);
    }

    const maxYear = _.get(myDatePickerOptions, 'maxYear', null);
    let disableSince = _.isNull(maxYear) ? null: { year: maxYear, month: 13, day: 1 };
    disableSince = _.get(myDatePickerOptions, 'disableSince', disableSince);
    this.maxDate = _.isNull(disableSince) ? null: new Date(disableSince.year, disableSince.month - 1, disableSince.day);

    const selectedDate = _.get(this.selectedDate, 'date', null);
    this.startDate = _.isNull(selectedDate) ? null: new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day);
  }

  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  writeValue(value: any) {
    if(_.isString(value)) {
      const dateArray = _.chain(value).split("/").map(element => {
        return parseInt(element);
      }).value();
      const date = new Date(dateArray[2], dateArray[0] - 1, dateArray[1]);
      this.control.setValue(date);
    }
    if(_.isNull(value)) {
      this.control.setValue(null);
    }
  }
}
